import { ArcLayer } from 'deck.gl';
import { cellToLatLng } from 'h3-js';
import { PARAMETROS_ADITIVOS } from './pulso';

// Arcos desde cada destino de trabajo considerado hacia los servicios que se
// alcanzan desde ahi (no desde la vivienda): la version real del segundo
// orden, en rojo y mas tenue que los arcos dorados del primer orden para no
// competir visualmente con ellos.
const COLOR_ARCO = [255, 90, 77];
// Mismo haz de barrido transitorio que los arcos dorados del primer orden:
// aparece al alcanzar el servicio y se apaga pocos minutos despues. Sin esto,
// seis destinos de trabajo por cientos de servicios cada uno apilan miles de
// lineas que saturan a blanco. Ademas de la estela corta, se acotan los arcos
// por celda de trabajo a sus servicios mas cercanos.
const VENTANA_ESTELA = 4.5;
const PICO_OPACIDAD = 0.42;
const MAX_ARCOS_POR_CELDA = 24;

function opacidadPorTiempo(minutos, t, pesoRelativo) {
  const trasElFrente = t - minutos;
  if (trasElFrente < 0 || trasElFrente > VENTANA_ESTELA) return 0;
  const factor = 1 - trasElFrente / VENTANA_ESTELA;
  return Math.round(PICO_OPACIDAD * (0.4 + 0.6 * pesoRelativo) * factor * 255);
}

// "destinosTrabajo" es el recorte [[work_h3, peso], ...] que tambien usa
// destinosSegundoOrden.js (ya acotado en App.js a los principales). "ttm" es
// datos.ttm completo: cubre TODAS las celdas de la grilla como origen (no
// solo las residenciales), asi que ttm[work_h3] ya trae los servicios
// alcanzables desde ese destino de trabajo sin recalcular nada.
// "amenitiesPorIdx" llega memoizado desde App (no se reconstruye por frame).
export function construyeCapaArcosSegundoOrden({ destinosTrabajo, ttm, amenitiesPorIdx, t }) {
  if (!destinosTrabajo || destinosTrabajo.length === 0) return null;

  const pesoMaximo = destinosTrabajo[0]?.[1] ?? 1;

  const arcos = [];
  for (const [workH3, peso] of destinosTrabajo) {
    const pesoRelativo = peso / pesoMaximo;
    const [lat, lon] = cellToLatLng(workH3);
    const origenPos = [lon, lat];
    const cercanos = (ttm[workH3] ?? [])
      .filter(([, minutos]) => minutos <= t && t - minutos <= VENTANA_ESTELA)
      .sort((a, b) => a[1] - b[1])
      .slice(0, MAX_ARCOS_POR_CELDA);
    for (const [idx, minutos] of cercanos) {
      const destino = amenitiesPorIdx.get(idx);
      if (!destino) continue;
      arcos.push({
        origenPos,
        targetPos: [destino.lon, destino.lat],
        minutos,
        pesoRelativo,
      });
    }
  }

  if (arcos.length === 0) return null;

  return new ArcLayer({
    id: 'arcos-segundo-orden',
    data: arcos,
    parameters: PARAMETROS_ADITIVOS,
    getSourcePosition: (d) => d.origenPos,
    getTargetPosition: (d) => d.targetPos,
    getSourceColor: (d) => [...COLOR_ARCO, opacidadPorTiempo(d.minutos, t, d.pesoRelativo)],
    getTargetColor: (d) => [...COLOR_ARCO, opacidadPorTiempo(d.minutos, t, d.pesoRelativo)],
    getWidth: 1,
    widthMinPixels: 1,
    updateTriggers: {
      getSourceColor: [t],
      getTargetColor: [t],
    },
  });
}
